import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import {
  ExecutiveSnapshot,
  FundedSummary,
  RequiringActionRow,
  RequiringActionStatus,
  SnapshotTiles,
} from '@partner/feature-modules/dashboard/domain/model/executive-snapshot.model';

/** 200 body of `GET api/dashboard/executive-snapshot` (contract v1); instants are ISO-8601 UTC strings. */
export interface ExecutiveSnapshotResponse {
  asOfUtc: string;
  period: { from: IsoDate; to: IsoDate };
  funded: FundedSummary;
  tiles: SnapshotTiles;
  requiringAction: RequiringActionRowResponse[];
  requiringActionTotal: number;
  inactivity: InactivityResponse;
  /** Envelope symmetry with the list response; always null on 200. */
  validationMessage: string | null;
}

export interface RequiringActionRowResponse {
  applicationId: number;
  businessName: string | null;
  amount: number;
  action: string;
  brokerStatus: RequiringActionStatus;
  waitingSinceUtc: string;
}

export interface InactivityResponse {
  lastSubmissionUtc: string | null;
  daysSinceLastSubmission: number | null;
}

const adaptRequiringActionRow = (row: RequiringActionRowResponse): RequiringActionRow => ({
  applicationId: row.applicationId,
  businessName: row.businessName ?? null,
  amount: row.amount,
  action: row.action,
  brokerStatus: row.brokerStatus,
  waitingSinceUtc: new Date(row.waitingSinceUtc),
});

export const adaptExecutiveSnapshot = (response: ExecutiveSnapshotResponse): ExecutiveSnapshot => ({
  asOfUtc: new Date(response.asOfUtc),
  period: { from: response.period.from, to: response.period.to },
  funded: { ...response.funded },
  tiles: {
    activeApplications: response.tiles.activeApplications,
    requiringAction: response.tiles.requiringAction,
    pendingActivation: { ...response.tiles.pendingActivation },
    funded: { ...response.tiles.funded },
  },
  requiringAction: (response.requiringAction ?? []).map(adaptRequiringActionRow),
  requiringActionTotal: response.requiringActionTotal,
  inactivity: {
    lastSubmissionUtc: response.inactivity?.lastSubmissionUtc ? new Date(response.inactivity.lastSubmissionUtc) : null,
    daysSinceLastSubmission: response.inactivity?.daysSinceLastSubmission ?? null,
  },
});
